import { Box, CircularProgress, Divider, FormControlLabel, Grid, Switch, TextField, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useStyles } from './style'
import { TenantEditSlice } from '../../../reducers'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { useTenantFormContext } from '../tenant-form-context'
import { useDispatch, useSelector } from 'react-redux'
import { useConfirm } from 'material-ui-confirm'
import { ActionButton, FormInputRow, Input } from '@emerald-works-nova/components'
import { Controller, useForm } from 'react-hook-form'
import { BannerStatic } from '../../../components'
import { usePromiseEvent } from '../../../hooks'

export const HelixMigrationSettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const { tenant } = useTenantFormContext()
  const confirm = useConfirm()
  const dispatch = useDispatch()
  const [isTogglingStatus, setIsTogglingStatus] = useState(false)

  const [getHelixMigrationConfig, updateHelixMigrationConfig, checkSharedLinkProgress] = useEvent([
    TenantEditSlice.eventBus.getHelixMigrationConfig,
    TenantEditSlice.eventBus.updateHelixMigrationConfig,
    TenantEditSlice.eventBus.checkSharedLinkProgress
  ])

  const toggleHelixConfigEnabled = usePromiseEvent('toggleHelixConfigEnabled')

  const [syncSharedLinks] = useEvent([{
    eventName: 'loadHelixSharedLinksForATenant',
    shouldTimeout: false,
    onSuccess: (payload) => {
      if (payload) {
        dispatch(TenantEditSlice.actions.setHelixMigrationConfig(payload))
      }
      BannerStatic.show({
        label: 'Shared links migration has successfully started, please wait a few moment to finish.'
      })
    },
    onError: (err) => {
      console.error('loadHelixSharedLinksForATenant', err)
      BannerStatic.show({
        label: 'Failed to start shared links migration, please try again.',
        isError: true
      })
    }
  }])

  const helixMigration = useSelector(TenantEditSlice.selectors.helixMigration)

  const [switchValue, setSwitchValue] = useState(
    helixMigration?.enabled || false
  )

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      helixSubscriptionUrl: helixMigration?.subscriptionUrl || '',
      alwaysRedirectToHome: helixMigration?.alwaysRedirectToHome || true
    }
  })

  useEventsOnViewLoad(() => {
    if (tenant.pk && !getHelixMigrationConfig.hasBeenTriggered) {
      getHelixMigrationConfig.trigger({
        tenantId: tenant.pk
      })
    }
  }, [tenant?.pk])

  useEffect(() => {
    if (helixMigration && !switchValue) {
      setSwitchValue(helixMigration?.enabled)
      form.setValue('helixSubscriptionUrl', helixMigration.subscriptionUrl)
      form.setValue('alwaysRedirectToHome', helixMigration.alwaysRedirectToHome)
    }
  }, [helixMigration, switchValue, form])

  const handleCheckProgress = useCallback(() => {
    checkSharedLinkProgress.trigger({
      tenantId: tenant.pk
    })
  }, [tenant.pk, checkSharedLinkProgress])

  const handleToggleIntegration = (evt, checked) => {
    if (!helixMigration) {
      setSwitchValue(checked)
      return
    }

    const labels = checked ? {
      title: 'Enable Helix URLs Migration',
      description: 'Are you sure you want enable Helix URLs Migration for this tenant?',
      confirmationText: 'Yes, enable it.',
      cancellationText: 'No, Thanks.'
    } : {
      title: 'Disable Helix URLs Migration',
      description: 'Are you sure you want disable Helix URLs Migration for this tenant?',
      confirmationText: 'Yes, disable it.',
      cancellationText: 'No, Thanks.'
    }

    confirm({
      title: labels.title,
      description: labels.description,
      confirmationText: labels.confirmationText,
      cancellationText: labels.cancellationText,
      titleProps: {
        'data-test': 'toggle-migration-confirmation-title'
      },
      confirmationButtonProps: {
        'data-test': 'confirm-toggle-migration-button'
      },
      cancellationButtonProps: {
        'data-test': 'cancel-toggle-migration-button'
      }
    }).then(async () => {
      try {
        setIsTogglingStatus(true)
        const config = await toggleHelixConfigEnabled({ tenantId: tenant.pk })
        BannerStatic.show({ label: 'Helix Migration settings have been successfully updated' })
        dispatch(TenantEditSlice.actions.toggleHelixConfigEnabled(config))
      } catch (err) {
        console.error('toggleHelixConfigEnabled', err)
        BannerStatic.show({ label: 'Failed to update Helix Migration settings', isError: true })
      } finally {
        setSwitchValue(checked)
        setIsTogglingStatus(false)
      }
    }).catch(() => {})
  }

  const handleSave = () => {
    updateHelixMigrationConfig.trigger({
      tenantId: tenant.pk,
      helixSubscriptionUrl: form.getValues().helixSubscriptionUrl,
      alwaysRedirectToHome: form.getValues().alwaysRedirectToHome
    })
  }

  const handleSyncSharedLinks = () => {
    confirm({
      title: 'Sync URLs',
      description: 'This action may take a few minutes to complete. Do you want to continue?',
      confirmationText: 'Yes, continue.',
      cancellationText: 'No, thanks.',
      titleProps: {
        'data-test': 'sync-shared-links-confirmation-title'
      },
      confirmationButtonProps: {
        'data-test': 'confirm-sync-shared-links-button'
      },
      cancellationButtonProps: {
        'data-test': 'cancel-sync-shared-links-button'
      }
    }).then(() => {
      syncSharedLinks.trigger({ tenantId: tenant.pk })
    }).catch(() => { })
  }

  const isLoading = getHelixMigrationConfig.isWorking ||
    updateHelixMigrationConfig.isWorking ||
    toggleHelixConfigEnabled.isWorking ||
    checkSharedLinkProgress.isWorking ||
    isTogglingStatus

  const sharedLinkMigrationStatus = useMemo(() => {
    if (!helixMigration || !helixMigration.started) return 'Not started'
    if (helixMigration.isError) return 'Error'
    if (helixMigration.isRunning) return 'Running'
    return 'Finished'
  }, [helixMigration])

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderRadius='undefinedpx'
          borderColor='#E0E0E0'
          display='flex'
          style={{
            justifyContent: 'space-between',
            flexDirection: 'column',
            padding: spacing(1, 4)
          }}
        >
          <Box display='flex' flexDirection='row'>
            <Typography variant='body2' style={{ marginTop: spacing(3.5) }}>
              Helix Migration
            </Typography>
            <Box
              display='flex'
              style={{
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                flex: 1,
                padding: spacing(1)
              }}
            >
              <Box style={{ padding: spacing(2, 0.5) }}>
                {getHelixMigrationConfig.isWorking
                  ? <CircularProgress size={24} />
                  : (
                    <Switch
                      color='primary'
                      data-test='api-integration-feature-toggle'
                      checked={switchValue}
                      onChange={handleToggleIntegration}
                      disabled={isLoading}
                    />
                  )}
              </Box>
            </Box>
          </Box>

          {switchValue && (
            <>
              <Divider style={{ marginBottom: spacing(1) }} />

              <Grid item xs={12}>
                <FormInputRow
                  title='Subscription URL'
                  description='SubscriptionURL exactly as found on Helix Admin.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='Subscription URL'
                      name='helixSubscriptionUrl'
                      control={form.control}
                    />
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <FormInputRow
                  title='Redirect to Homepage'
                  description='Turn this toggle on if you want all 2.0 links to redirect the user to the 3.0 homepage instead of the mapped pages.'
                  smallFormTitles
                  InputComponent={
                    <Controller
                      name='alwaysRedirectToHome'
                      control={form.control}
                      render={({ field: { name, onChange, value } }) => (
                        <FormControlLabel
                          label='Redirect to Homepage'
                          labelPlacement='start'
                          classes={{ label: classes.exclusiveLabel }}
                          control={
                            <Switch
                              name={name}
                              checked={value}
                              onChange={onChange}
                              color='primary'
                            />
                          }
                        />
                      )}
                    />

                  }
                />

              </Grid>

              <Grid item xs={12}>
                <FormInputRow
                  title='URLs Migration Status'
                  description='Here you can see the status of the URL migration from 2.0 to 3.0.'
                  smallFormTitles
                  InputComponent={
                    <TextField
                      label='URLs Migration Status'
                      value={sharedLinkMigrationStatus}
                      disabled
                      sx={{ width: '100%' }}
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  }
                />
              </Grid>

              {helixMigration && helixMigration.started && (
                <Grid item xs={12}>
                  <FormInputRow
                    title='Most Recent URL Migrated'
                    description='Here you can see the most recent URL that was migrated to 3.0. Any URLs generated after this date are not included in the redirect list. If a user clicks on one of these newer URLs, they will encounter an error. Click Sync to update the list with the latest URLs.'
                    smallFormTitles
                    InputComponent={
                      <TextField
                        label='Most Recent URL Migrated'
                        value={helixMigration.lastMigratedSharedLinkCreatedDate}
                        sx={{ width: '100%' }}
                        disabled
                        InputProps={{
                          readOnly: true
                        }}
                      />
                    }
                  />
                </Grid>
              )}
            </>
          )}
          {switchValue && (
            <>
              <Divider
                style={{ marginBottom: spacing(1), paddingTop: spacing(2) }}
              />

              <Box
                className={classes.ctnControls}
                display='flex'
                justifyContent='flex-end'
              >
                {helixMigration && !helixMigration.isRunning && (
                  <ActionButton
                    onClick={handleSyncSharedLinks}
                    disabled={isLoading}
                    isWorking={syncSharedLinks.isWorking}
                    dataTest='helix-migration-sync-shared-links-button'
                  >
                    Sync Shared Links
                  </ActionButton>
                )}

                {helixMigration?.isRunning && (
                  <ActionButton
                    onClick={handleCheckProgress}
                    disabled={isLoading}
                    isWorking={checkSharedLinkProgress.isWorking}
                    dataTest='helix-migration-check-progress'
                  >
                    Check Progress
                  </ActionButton>
                )}

                <ActionButton
                  onClick={handleSave}
                  disabled={isLoading || updateHelixMigrationConfig.isWorking}
                  variant='primary'
                  dataTest='helix-migration-save-button'
                >
                  Save
                </ActionButton>
              </Box>
            </>
          )}
        </Box>

      </Grid>

    </Grid>
  )
}
